import { Link } from 'react-router';
import type { Product } from '@/types';
import { Price } from '@/components/Price';
import { FramedImage } from '@/components/FramedImage';
import { useI18n } from '@/client/i18n/I18nContext';
import { localizeProduct } from '@/lib/i18n-content';
import { isSoldPiece, showsSoldOut } from '@/lib/catalog/availability';
import { lp } from '@/client/i18n/locale';

/**
 * Quiet, editorial product card. Deliberately minimal (Lemaire-style): photo,
 * brand, name and price only — no add-to-cart, subtitle or specs here. The one
 * label is the availability state: «En reasortimiento» (pre-order, still for
 * sale) or, for a sold-out piece without reasortimiento, «Agotado» / «Pieza
 * vendida» (listed after the rest, see listingOrder). Every detail lives on the
 * product page. The whole card links to it.
 */
export function ProductCard({ product: raw }: { product: Product }) {
  const { t, locale } = useI18n();
  const product = localizeProduct(raw, locale);
  const href = lp(`/shop/${product.category}/${product.slug}`);
  const isEnquiry = product.checkoutMode === 'enquiry';
  const isPreorder = raw.metadata.preorder === true;
  // Never for a pre-order (reasortimiento stays buyable) nor an enquiry piece.
  const soldOut = showsSoldOut(raw);

  return (
    <article className={soldOut ? 'card card--sold-out' : 'card'}>
      <Link to={href} className="card__media" aria-label={product.name}>
        {product.image ? (
          <FramedImage
            src={product.image}
            alt={product.name}
            loading="lazy"
            fallback={product.brand ?? 'CARO'}
          />
        ) : (
          <span className="card__placeholder">{product.brand ?? 'CARO'}</span>
        )}
      </Link>
      <Link to={href} className="card__body">
        {isPreorder ? (
          <span className="card__preorder">{t('product.preorderBadge')}</span>
        ) : soldOut ? (
          <span className="card__sold-out">
            {isSoldPiece(raw) ? t('pdp.soldPieceTitle') : t('pdp.soldOutTitle')}
          </span>
        ) : null}
        {product.brand ? <span className="card__brand">{product.brand}</span> : null}
        <h3 className="card__name">{product.name}</h3>
        <div className="card__footer">
          {isEnquiry ? (
            <span className="price price--tbc">{t('common.onRequest')}</span>
          ) : (
            <Price
              cents={product.priceCents}
              currency={product.currency}
              compareAtCents={product.compareAtPriceCents}
              tbc={!product.dataVerified || product.priceCents <= 0}
            />
          )}
        </div>
      </Link>
    </article>
  );
}
